/**
 * Video player specific types (hybrid approach)
 *
 * Note: Qualities, audio tracks, and subtitles are handled via HLS manifest,
 * not through separate PlaybackInfo fields. The player reads them directly
 * from hls.js:
 *
 * - Qualities: hls.levels / hls.currentLevel (from EXT-X-STREAM-INF)
 * - Audio tracks: hls.audioTracks / hls.audioTrack (from EXT-X-MEDIA TYPE=AUDIO)
 * - Subtitles: hls.subtitleTracks / hls.subtitleTrack (from EXT-X-MEDIA TYPE=SUBTITLES)
 */

import type { VideoAssetListItem, VideoPlaybackInfo } from '../types/asset'
import type { MediaPlayerState, PlayerCallbacks, PlayerConfig } from '../types/player'
import type { SourceDownload } from '../types/embed'
import type { VASTAdConfig } from './services/vast/types'

// =============================================================================
// HLS TRACK INFO (exposed from hls.js for UI)
// =============================================================================

export interface QualityLevel {
  index: number
  height: number
  width: number
  bitrate: number
  name: string // e.g., "720p", "1080p"
}

export interface AudioTrack {
  index: number
  language: string
  name: string
  isDefault: boolean
  isHearingImpaired: boolean
}

export interface SubtitleTrack {
  index: number
  language: string
  name: string
  isDefault: boolean
  isAutoGenerated: boolean
}

// =============================================================================
// CONFIG
// =============================================================================

export interface VideoPlayerConfig extends PlayerConfig<VideoAssetListItem> {
  // Direct media URL (bypasses the items/PlaybackInfo system).
  // When set, the player plays this URL directly: `.m3u8` → HLS.js, any
  // other URL → native <video> (mp4/webm/ogg/mov, etc.). Features that
  // depend on the HLS manifest (quality, audio tracks, subtitles,
  // chapters, downloads) are simply unavailable in this mode.
  src?: string

  // Quality (actual levels come from HLS manifest via hls.levels)
  autoQuality?: boolean // -1 = auto in hls.currentLevel
  preferredMaxHeight?: number // e.g. 720, 1080 - will pick closest available
  ecoModeDefault?: boolean // start in eco mode (auto-quality capped at 720p)

  // Playback
  playbackSpeed?: number
  muted?: boolean
  startAt?: number // seconds — seek to this position on first load

  // Audio tracks (preference, actual tracks come from HLS)
  preferredAudioLanguage?: string

  // Subtitles (preference, actual tracks come from HLS)
  subtitlesEnabled?: boolean
  preferredSubtitleLanguage?: string
  subtitlesAutoStart?: boolean

  // UI
  pip?: boolean // picture-in-picture enabled
  enableFullscreen?: boolean
  showChapters?: boolean
  showEcoMode?: boolean
  showInfoButton?: boolean
  showHomepageInfo?: boolean // show title/duration on homepage overlay
  posterFit?: 'contain' | 'cover' // how the poster thumbnail fits the player area

  // Downloads
  allowSourceDownload?: boolean
  playbackToken?: string
  /** Mints the source link in place of the `visitor` route — the in-app player's. */
  sourceDownload?: SourceDownload

  // Ads (VAST) — the publisher's forced skip rides on each VASTAdConfig, not here, because a
  // mid-roll from another tag could carry its own.
  adsConfig?: {
    preRoll?: VASTAdConfig[]
    midRolls?: Array<{ time: number; ads: VASTAdConfig[] }>
    postRoll?: VASTAdConfig[]
  }
}

// =============================================================================
// STATE
// =============================================================================

export interface VideoPlayerState extends MediaPlayerState {
  // Quality (from hls.js)
  currentQualityIndex: number // -1 = auto
  isAutoQuality: boolean
  availableQualities: QualityLevel[]

  // Playback
  playbackSpeed: number

  // Audio tracks (from hls.js)
  currentAudioTrackIndex: number
  availableAudioTracks: AudioTrack[]

  // Subtitles (from hls.js)
  subtitlesEnabled: boolean
  currentSubtitleTrackIndex: number // -1 = disabled
  availableSubtitleTracks: SubtitleTrack[]

  // UI state
  isFullscreen: boolean
  isPip: boolean
  currentChapterIndex: number // -1 = no chapter
}

// =============================================================================
// OPTIONS
// =============================================================================

export interface VideoPlayerOptions {
  config: VideoPlayerConfig
  initialPlaybackInfo?: VideoPlaybackInfo
  poster?: string
}

// =============================================================================
// EVENTS (extends base PlayerCallbacks)
// =============================================================================

export interface VideoPlayerCallbacks extends PlayerCallbacks<VideoAssetListItem> {
  onQualityChange?: (level: QualityLevel | null) => void // null = auto
  onAutoQualityChange?: (isAuto: boolean) => void
  onAudioTrackChange?: (track: AudioTrack) => void
  onSubtitleChange?: (track: SubtitleTrack | null) => void // null = disabled
  onPlaybackSpeedChange?: (speed: number) => void
  onChapterChange?: (chapterIndex: number, chapterTitle: string) => void
  onPipChange?: (isPip: boolean) => void
}

// =============================================================================
// PANELS
// =============================================================================

export enum PanelType {
  CHAPTERS = 'chapters',
  PLAYLIST = 'playlist',
  TRANSCRIPT = 'transcript',
  FILES = 'files',
  DETAILS = 'details',
  INFO = 'info',
  SETTINGS = 'settings',
  QUALITY = 'quality',
  SUBTITLES = 'subtitles',
  AUDIO_TRACKS = 'audio-tracks',
  KEYBOARD_SHORTCUTS = 'keyboard-shortcuts',
}

export function isPanelType(value: string): value is PanelType {
  return Object.values(PanelType).includes(value as PanelType)
}

// =============================================================================
// COLLECTION DISPLAY MODE
// =============================================================================

export type CollectionDisplayMode = 'bottom-bar' | 'sidebar' | 'drawer'

// =============================================================================
// PLAYBACK SPEEDS
// =============================================================================

export const PLAYBACK_SPEEDS = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2] as const
export type PlaybackSpeed = (typeof PLAYBACK_SPEEDS)[number]
